import type { Middleware } from "redux";
import type { PriceRange } from "@b2bportal/core-types";
import type { AvailabilitySort } from "@b2bportal/rbc-activities-api";
import {
  ACTIVITIES_AVAILABILITY_PATH,
  navigateTo,
  ACTIVITY_URL_PARAM_KEYS,
} from "@overrides/utilities";
import { ActivityRatingValueEnum } from "@tenant/activities";
import dayjs from "dayjs";
import {
  type ActivitiesAvailabilityActionTypes,
  type ActivitiesAvailabilitySearchState,
  ActivitiesAvailabilityActions,
  type ActivitySearchDetails,
  initializeSearchState,
} from "..";
import type { ActivityFilterState } from "~core-activities/features";

// Type for URL Param Handler
type UrlParamHandler<T> = (payload: T) => {
  updatedUrl: URL;
  navigateWithReplace: boolean;
};

type ActionTypes = ActivitiesAvailabilityActionTypes;

type HandlerMap = {
  [K in ActionTypes["type"]]?: UrlParamHandler<
    (ActionTypes & { type: K })["payload"]
  >;
};

const updateSearchParam = (
  updatedUrl: URL,
  key: string,
  value: string | null
) => {
  if (value !== null) {
    updatedUrl.searchParams.set(key, value);
  } else {
    updatedUrl.searchParams.delete(key);
  }
};

const handleActivitySearchUpdate = ({
  currentUrl,
  search,
  replace,
}: {
  currentUrl: URL;
  search: ActivitySearchDetails;
  replace: boolean;
}) => {
  const { place, dateRange, travelers } = search;
  const updatedUrl = new URL(currentUrl);

  // Keep trailing slash if it exits on the current url.
  const hasTrailingSlash = currentUrl.pathname.endsWith("/");

  const destinationCode = encodeURIComponent(
    place.label +
      (place.subLabel != null && place.subLabel.length > 0
        ? `, ${place.subLabel}`
        : "")
  );
  updatedUrl.pathname = `${ACTIVITIES_AVAILABILITY_PATH}/${destinationCode}${hasTrailingSlash ? "/" : ""}`;

  updateSearchParam(
    updatedUrl,
    ACTIVITY_URL_PARAM_KEYS.FROM_DATE,
    dayjs(dateRange.from).format("YYYY-MM-DD")
  );
  updateSearchParam(
    updatedUrl,
    ACTIVITY_URL_PARAM_KEYS.UNTIL_DATE,
    dayjs(dateRange.until).format("YYYY-MM-DD")
  );

  updateSearchParam(
    updatedUrl,
    ACTIVITY_URL_PARAM_KEYS.ADULTS_COUNT,
    travelers != null ? travelers.toString() : null
  );

  return {
    updatedUrl,
    navigateWithReplace: replace,
  };
};

// Factory function to create the middleware with custom handlers
export const createActivityUrlMiddleware =
  (customHandlers: HandlerMap = {}): Middleware =>
  (_store) =>
  (next) =>
  (action: ActionTypes) => {
    // Skip URL handling in server environment
    if (typeof window === "undefined") {
      return next(action);
    }

    const currentUrl = new URL(window.location.toString());

    // Default handlers for each action
    const defaultHandlers: HandlerMap = {
      [initializeSearchState.fulfilled.type]: (
        payload: ActivitiesAvailabilitySearchState
      ) => {
        if (payload.requestedSearch != null)
          return handleActivitySearchUpdate({
            currentUrl,
            search: payload.requestedSearch,
            replace: true,
          });

        return {
          updatedUrl: currentUrl,
          navigateWithReplace: true,
        };
      },
      [ActivitiesAvailabilityActions.setRequestedSearch.type]: (
        payload: ActivitySearchDetails
      ) => {
        return handleActivitySearchUpdate({
          currentUrl,
          search: payload,
          replace: false,
        });
      },
      [ActivitiesAvailabilityActions.setPriceFilter.type]: (
        payload: PriceRange
      ) => {
        const updatedUrl = new URL(currentUrl);

        updateSearchParam(
          updatedUrl,
          ACTIVITY_URL_PARAM_KEYS.PRICE_RANGE_MIN,
          payload.min.toString()
        );
        updateSearchParam(
          updatedUrl,
          ACTIVITY_URL_PARAM_KEYS.PRICE_RANGE_MAX,
          payload.max.toString()
        );

        return {
          updatedUrl,
          navigateWithReplace: true,
        };
      },
      // TODO: Update these handlers for activities-specific filters
      // [ActivitiesAvailabilityActions.setCategoriesFilter.type]: (
      //   payload: string[]
      // ) => {
      //   const updatedUrl = new URL(currentUrl);
      //   updateSearchParam(
      //     updatedUrl,
      //     ACTIVITY_URL_PARAM_KEYS.CATEGORIES,
      //     payload.length ? payload.join(",") : null
      //   );
      //   return {
      //     updatedUrl,
      //     navigateWithReplace: true,
      //   };
      // },
      [ActivitiesAvailabilityActions.setSort.type]: (
        payload: AvailabilitySort
      ) => {
        const updatedUrl = new URL(currentUrl);
        updateSearchParam(
          updatedUrl,
          ACTIVITY_URL_PARAM_KEYS.SORT_BY,
          payload != null ? payload : null
        );
        return {
          updatedUrl,
          navigateWithReplace: true,
        };
      },
      [ActivitiesAvailabilityActions.setCancellationFilter.type]: (
        payload: boolean
      ) => {
        const updatedUrl = new URL(currentUrl);
        updateSearchParam(
          updatedUrl,
          ACTIVITY_URL_PARAM_KEYS.FREE_CANCELLATION,
          payload ? "true" : null
        );
        return {
          updatedUrl,
          navigateWithReplace: true,
        };
      },
      [ActivitiesAvailabilityActions.resetFilters.type]: (_: undefined) => {
        const updatedUrl = new URL(currentUrl);
        [
          ACTIVITY_URL_PARAM_KEYS.PRICE_RANGE_MIN,
          ACTIVITY_URL_PARAM_KEYS.PRICE_RANGE_MAX,
          ACTIVITY_URL_PARAM_KEYS.FREE_CANCELLATION,
          ACTIVITY_URL_PARAM_KEYS.RATING,
          ACTIVITY_URL_PARAM_KEYS.DURATION,
          ACTIVITY_URL_PARAM_KEYS.START_TIME,
          ACTIVITY_URL_PARAM_KEYS.USER_RATING,
        ].forEach((param) => updatedUrl.searchParams.delete(param));
        return {
          updatedUrl,
          navigateWithReplace: true,
        };
      },
      [ActivitiesAvailabilityActions.setAllFilters.type]: (
        payload: ActivityFilterState
      ) => {
        const updatedUrl = new URL(currentUrl);
        const {
          maxPrice,
          freeCancellation,
          rating,
          duration,
          startTime,
          userRating,
        } = payload;

        // Price filter - set max only if not the default
        updateSearchParam(
          updatedUrl,
          ACTIVITY_URL_PARAM_KEYS.PRICE_RANGE_MAX,
          maxPrice !== Number.MAX_SAFE_INTEGER ? maxPrice.toString() : null
        );

        // Free cancellation - set only if true
        updateSearchParam(
          updatedUrl,
          ACTIVITY_URL_PARAM_KEYS.FREE_CANCELLATION,
          freeCancellation ? "true" : null
        );

        // Note: Categories and popularFeatureTags NOT added to URL
        // They require API response data to map names to tag objects

        // Rating - set only if not default (FourPointFive)
        updateSearchParam(
          updatedUrl,
          ACTIVITY_URL_PARAM_KEYS.RATING,
          rating !== ActivityRatingValueEnum.FourPointFive ? rating : null
        );

        // Duration - comma-separated enum array
        updateSearchParam(
          updatedUrl,
          ACTIVITY_URL_PARAM_KEYS.DURATION,
          duration.length > 0 ? duration.join(",") : null
        );

        // Start time - comma-separated enum array
        updateSearchParam(
          updatedUrl,
          ACTIVITY_URL_PARAM_KEYS.START_TIME,
          startTime.length > 0 ? startTime.join(",") : null
        );

        // User rating - numeric value
        updateSearchParam(
          updatedUrl,
          ACTIVITY_URL_PARAM_KEYS.USER_RATING,
          userRating != null ? userRating.toString() : null
        );

        return {
          updatedUrl,
          navigateWithReplace: true,
        };
      },
    };

    const mergedHandlers = {
      ...defaultHandlers,
      ...customHandlers,
    };

    const handler = mergedHandlers[action.type] as
      | UrlParamHandler<any>
      | undefined;

    if (handler != null) {
      const { updatedUrl, navigateWithReplace } = handler(action.payload);
      if (updatedUrl.toString() !== currentUrl.toString()) {
        navigateTo(
          `${updatedUrl.pathname}${updatedUrl.search}${updatedUrl.hash}`,
          {
            replace: navigateWithReplace,
          }
        );
      }
    }
    return next(action);
  };
